import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Button } from '@vaiu/ui/button';
import { LoadingSpinner } from '@vaiu/ui';
import {
  ensureStripeJsLoaded,
  resetStripeJsLoader,
} from '@/config/stripe';

interface StripeProviderProps {
  children: React.ReactNode;
}

const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  const elementsOptions = useMemo(
    () => ({
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#0066cc',
          colorBackground: '#ffffff',
          colorText: '#424770',
          colorDanger: '#df1b41',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          spacingUnit: '4px',
          borderRadius: '6px',
        },
      },
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    ensureStripeJsLoaded()
      .then((instance) => {
        if (!cancelled) {
          setStripe(instance);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.error('Failed to initialize Stripe:', err);
          const message =
            err instanceof Error
              ? err.message
              : 'Unable to load the card form. Please try again.';
          setError(message);
          setStripe(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  const handleRetry = useCallback(() => {
    resetStripeJsLoader(true);
    setLoadAttempt((n) => n + 1);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner text="Initializing card processing..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          <div className="space-y-3">
            <p>
              <strong>Couldn&apos;t load card payments</strong>
            </p>
            <p className="text-sm">{error}</p>
            <Button type="button" variant="outline" size="sm" onClick={handleRetry}>
              Try again
            </Button>
            {import.meta.env.DEV && (
              <p className="text-xs text-muted-foreground">
                Dev: ensure pay platform `/config/stripe` responds or set
                VITE_STRIPE_PUBLISHABLE_KEY.
              </p>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (!stripe) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          <div className="space-y-3">
            <p>Card payments are not available right now.</p>
            <Button type="button" variant="outline" size="sm" onClick={handleRetry}>
              Try again
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Elements stripe={stripe} options={elementsOptions}>
      {children}
    </Elements>
  );
};

export default StripeProvider;
